/* [Harmonee AI Inc. — TRADE SECRET — proprietary; do not distribute. See NOTICE / LICENSE.] */
// AccountMenu — the account hub, anchored to the profile chip in the topbar.
// One place for identity: every connected Google account (with per-account
// disconnect and "Add Google account"), plus the Outlook connection.
// Controlled component: parent owns the open state (the chip, and the small
// provider pills, all toggle it).
import React, { useEffect, useRef } from 'react';

export const GIcon = () => (
  <svg className="g-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export const MsIcon = () => (
  <svg className="ms-icon" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export default function AccountMenu({
  open,
  onClose,
  anchorRef,
  googleAccounts = [],
  adding,
  onConnectGoogle,
  onAddGoogle,
  onRemoveGoogle,
  msAuthed,
  msUser,
  onConnectMs,
  onDisconnectMs,
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          !(anchorRef?.current && anchorRef.current.contains(e.target))) {
        onClose();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return (
    <div className="hm-gacct-menu hm-acct-hub" role="menu" aria-label="Connected accounts" ref={menuRef}>
      <div className="hm-gacct-title">Google accounts</div>
      {googleAccounts.length === 0 && (
        <button type="button" className="hm-gacct-add" onClick={() => { onClose(); onConnectGoogle(); }}>
          <GIcon /> Connect Google
        </button>
      )}
      {googleAccounts.map((a) => (
        <div key={a.email} className="hm-gacct-row" role="menuitem">
          {a.picture
            ? <img className="hm-gacct-avatar" src={a.picture} alt="" referrerPolicy="no-referrer" />
            : <span className="hm-gacct-avatar hm-gacct-avatar-fallback">{a.initials || (a.email[0] || '?').toUpperCase()}</span>}
          <span className="hm-gacct-meta">
            <span className="hm-gacct-name">{a.name || a.email.split('@')[0]}</span>
            <span className="hm-gacct-email">{a.email}</span>
          </span>
          <button
            type="button"
            className="hm-gacct-remove"
            onClick={() => onRemoveGoogle(a.email)}
            title={`Disconnect ${a.email}`}
          >
            Disconnect
          </button>
        </div>
      ))}
      {googleAccounts.length > 0 && (
        <button type="button" className="hm-gacct-add" onClick={onAddGoogle} disabled={adding}>
          <GIcon /> {adding ? 'Opening Google…' : 'Add Google account'}
        </button>
      )}

      <div className="hm-gacct-title hm-hub-section">Outlook</div>
      {msAuthed ? (
        <div className="hm-gacct-row" role="menuitem">
          <span className="hm-gacct-avatar hm-gacct-avatar-fallback"><MsIcon /></span>
          <span className="hm-gacct-meta">
            <span className="hm-gacct-name">{msUser?.name || 'Outlook'}</span>
            <span className="hm-gacct-email">{msUser?.email || 'connected'}</span>
          </span>
          <button type="button" className="hm-gacct-remove" onClick={onDisconnectMs} title="Disconnect Outlook">
            Disconnect
          </button>
        </div>
      ) : (
        <button type="button" className="hm-gacct-add" onClick={() => { onClose(); onConnectMs(); }}>
          <MsIcon /> Connect Outlook
        </button>
      )}
    </div>
  );
}
